/**
 * Utility functions for calculating business hours (excluding weekends)
 * For the Kellanova implementation
 */

/**
 * Calculate business hours between two dates, excluding weekends (Saturday and Sunday)
 * @param {Date} startDate - The start date
 * @param {Date} endDate - The end date
 * @returns {number} - Number of business hours between the dates
 */
export const calculateBusinessHours = (startDate, endDate) => {
  if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return null;
  }

  if (endDate <= startDate) {
    return 0;
  }

  let totalHours = 0;
  let currentDate = new Date(startDate);
  const finalDate = new Date(endDate);

  while (currentDate < finalDate) {
    const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
    
    // Skip weekends (Saturday = 6, Sunday = 0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      // Calculate hours for this day
      let startOfDay = new Date(currentDate);
      let endOfDay = new Date(currentDate);
      endOfDay.setHours(23, 59, 59, 999);

      // Adjust start time if this is the first day
      if (currentDate.toDateString() === startDate.toDateString()) {
        startOfDay = new Date(startDate);
      }

      // Adjust end time if this is the last day
      if (currentDate.toDateString() === finalDate.toDateString()) {
        endOfDay = new Date(finalDate);
      }

      // Add hours for this business day
      const hoursThisDay = (endOfDay - startOfDay) / (1000 * 60 * 60);
      totalHours += hoursThisDay;
    }

    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
    currentDate.setHours(0, 0, 0, 0);
  }

  return Math.max(0, totalHours);
};

/**
 * Kellanova time performance thresholds (in business hours)
 */
export const KELLANOVA_THRESHOLDS = {
  good: 72,      // 0-72 hours is good
  moderate: 121, // 73-121 hours is moderate
  // 121+ hours is poor
};

/**
 * Get performance category based on business hours
 * @param {number} businessHours - Number of business hours
 * @returns {string} - Performance category: 'good', 'moderate', or 'poor'
 */
export const getPerformanceCategory = (businessHours) => {
  if (businessHours === null || businessHours === undefined) {
    return 'unknown';
  }

  if (businessHours <= KELLANOVA_THRESHOLDS.good) {
    return 'good';
  } else if (businessHours <= KELLANOVA_THRESHOLDS.moderate) {
    return 'moderate';
  } else {
    return 'poor';
  }
};

/**
 * Get performance color based on category
 * @param {string} category - Performance category
 * @returns {object} - Object with color and backgroundColor properties
 */
export const getPerformanceColor = (category) => {
  switch (category) {
    case 'good':
      return {
        color: 'rgba(39, 174, 96, 0.85)',
        backgroundColor: 'rgba(39, 174, 96, 0.1)',
        textColor: 'text-green-700',
        bgColor: 'bg-green-50'
      };
    case 'moderate':
      return {
        color: 'rgba(241, 196, 15, 0.85)',
        backgroundColor: 'rgba(241, 196, 15, 0.1)',
        textColor: 'text-yellow-700',
        bgColor: 'bg-yellow-50'
      };
    case 'poor':
      return {
        color: 'rgba(231, 76, 60, 0.85)',
        backgroundColor: 'rgba(231, 76, 60, 0.1)',
        textColor: 'text-red-700',
        bgColor: 'bg-red-50'
      };
    default:
      return {
        color: 'rgba(201, 203, 207, 0.85)',
        backgroundColor: 'rgba(201, 203, 207, 0.1)',
        textColor: 'text-gray-700',
        bgColor: 'bg-gray-50'
      };
  }
};